import { ThemeId } from '@stadionhq-v3/core';
import { NewsCarouselProps } from '@stadionhq-v3/base-ui';
import {
  ArticlePage,
  Link,
  NewsCarouselBlock,
} from '@graphql/__generated__/graphql';
import { Locale } from '@core/constants/locales.constants';
import { isRtlLocale } from '@utils/locales.utils/locales.utils';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { Translations } from '@lib/dictionary-keys/__generated__';
import { Fragment } from 'react';
import { ArticleCardRenderer } from '@components/helpers/article-card-renderer';
import { ChevronLeft, ChevronRight } from '@components/icons';
import { generateInternalLinkUrl } from '@utils/paths/generateInternalLinkUrl.utils';
import { linkTransform } from '../atoms';

const newsCarouselBlockTransform = (
  {
    newsCarouselTitle,
    subTitle,
    link,
    theme,
    featuredGridArticle,
    displayAsGrid = false,
  }: CleanContentful<NewsCarouselBlock>,
  locale: Locale,
  dictionary: {
    previousAction: Translations['common']['action']['previous'];
    nextAction: Translations['common']['action']['next'];
    featured?: Translations['news']['label']['featured'];
    membersOnly?: Translations['articleCard']['label']['membersOnly'];
  },
  newsCards: CleanContentful<ArticlePage>[] | undefined
): NewsCarouselProps => {
  const cardsAsGrid = newsCards?.slice(0, 6);

  // if the featured card is in the first 6 cards, we need to remove it from the array
  const cardsToBeRenderedAsGrid = cardsAsGrid?.filter(
    (card) => card.id !== featuredGridArticle?.id
  );

  // if card is removed from the array, add the next card from the newsCards array
  if (
    cardsToBeRenderedAsGrid &&
    featuredGridArticle &&
    newsCards &&
    cardsToBeRenderedAsGrid.length < 6
  ) {
    const nextCard = newsCards[6];
    if (nextCard) {
      cardsToBeRenderedAsGrid.push(nextCard);
    }
  }

  const cardsToBeRendered = displayAsGrid ? cardsToBeRenderedAsGrid : newsCards;

  return {
    title: newsCarouselTitle || '',
    subTitle: subTitle || '',
    cards: cardsToBeRendered?.filter(Boolean).map((card) => (
      <Fragment key={card.id}>
        <ArticleCardRenderer
          article={card}
          locale={locale}
          dictionary={dictionary}
        />
      </Fragment>
    )),
    link: link?.url
      ? linkTransform(link, locale)
      : link
        ? (() => {
            const internal = generateInternalLinkUrl(
              link as CleanContentful<Link>,
              locale
            );
            return internal && internal.url
              ? {
                  ...internal,
                  title: internal.title || '',
                  url: internal.url || '',
                }
              : undefined;
          })()
        : undefined,
    displayAsGrid,
    featuredCard: featuredGridArticle ? (
      <ArticleCardRenderer
        // because its already selected as featured we can override
        article={{ ...featuredGridArticle, isFeatured: true }}
        locale={locale}
        dictionary={dictionary}
        size="xlarge" // fomratting choice from the designs
      />
    ) : null,
    theme: {
      themeId: theme?.themeId as ThemeId,
      title: theme?.title as string,
    },
    previousButton: {
      text: dictionary.previousAction,
      iconBefore: isRtlLocale(locale) ? <ChevronRight /> : <ChevronLeft />,
    },
    nextButton: {
      text: dictionary.nextAction,
      iconBefore: isRtlLocale(locale) ? <ChevronLeft /> : <ChevronRight />,
    },
    options: {
      direction: isRtlLocale(locale) ? 'rtl' : 'ltr',
    },
  };
};

export default newsCarouselBlockTransform;
